import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { FotosManager } from '@/features/analisis/common/components/FotosManager';
import type { FotoTemporal, FotoTemporalDraft } from '@/features/analisis/common/fotosTypes';
import type { AnalisisDraft } from '@/features/analisis/common/hooks/useAnalisisDraft';
import { useAnalisisDraft } from '@/features/analisis/common/hooks/useAnalisisDraft';
import { fotosTemporalesApi } from '@/features/analisis/common/api/fotosTemporalesApi';
import { formatNumeroAnalisis } from '@/features/analisis/utils/formatNumeroAnalisis';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage } from '@/lib/apiError';
import { TipoAnalisis } from '@/types/enums';

import {
  useCreateEctoparasitos,
  useEctoparasitos,
  useEditarCaptionEctoparasitos,
  useEliminarFotoEctoparasitos,
  useUpdateEctoparasitos,
  useUploadFotoEctoparasitos,
} from '../hooks/useEctoparasitos';
import { ectoparasitosFormSchema } from '../schemas';
import type { EctoparasitosFormValues } from '../schemas';
import { EctoparasitosDatosForm } from './EctoparasitosDatosForm';
import { EctoparasitosResultadoForm } from './EctoparasitosResultadoForm';

type TabId = 'datos' | 'resultado';

const DEFAULT_VALUES: EctoparasitosFormValues = {
  fecha_analisis: new Date(),
  identificacion_muestras: null,
  muestra: {
    categoria: '',
    muestra: '',
    valor: 'negativo',
    descripcion_resultado: null,
    observacion: null,
  },
};

const DATOS_FIELDS: (keyof EctoparasitosFormValues)[] = [
  'fecha_analisis',
  'identificacion_muestras',
];

type EctoparasitosDraftData = Omit<EctoparasitosFormValues, 'fecha_analisis'> & {
  fecha_analisis: string;
  fotosTemporales: FotoTemporalDraft[];
};

interface EctoparasitosFormDialogProps {
  open: boolean;
  onClose: () => void;
  uidProtocolo: string;
  uidAnalisis?: string;
  numeroProtocolo?: number;
  establecimientoNombre?: string;
}

export function EctoparasitosFormDialog({
  open,
  onClose,
  uidProtocolo,
  uidAnalisis: uidAnalisisProp,
  numeroProtocolo,
  establecimientoNombre,
}: EctoparasitosFormDialogProps): JSX.Element {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const notify = useNotify();

  // After create, we keep the dialog open with the new UID
  const [activeUidAnalisis, setActiveUidAnalisis] = useState<string | undefined>(uidAnalisisProp);
  const [activeNumeroAnalisis, setActiveNumeroAnalisis] = useState<number | null>(null);
  const isEdit = !!activeUidAnalisis;

  const [tabActiva, setTabActiva] = useState<TabId>('datos');
  const [fotosTemporales, setFotosTemporales] = useState<FotoTemporal[]>([]);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [pendingDraft, setPendingDraft] = useState<AnalisisDraft<EctoparasitosDraftData> | null>(null);

  const { saveDraft, loadDraft, clearDraft } = useAnalisisDraft<EctoparasitosDraftData>('ectoparasitos');

  const ectoQuery = useEctoparasitos(uidProtocolo, activeUidAnalisis);
  const createMutation = useCreateEctoparasitos();
  const updateMutation = useUpdateEctoparasitos();

  const methods = useForm<EctoparasitosFormValues>({
    resolver: zodResolver(ectoparasitosFormSchema),
    defaultValues: { ...DEFAULT_VALUES, fecha_analisis: new Date() },
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, errors, submitCount },
  } = methods;

  const watchedAll = useWatch({ control });

  // Sync activeUidAnalisis when prop changes (e.g. dialog re-opened for a different analysis)
  useEffect(() => {
    setActiveUidAnalisis(uidAnalisisProp);
    setActiveNumeroAnalisis(null);
  }, [uidAnalisisProp]);

  // Reset on open in create mode
  useEffect(() => {
    if (open && !uidAnalisisProp) {
      const d = loadDraft(uidProtocolo);
      setPendingDraft(d);
      setActiveUidAnalisis(undefined);
      setActiveNumeroAnalisis(null);
      setFotosTemporales([]);
      setTabActiva('datos');
      setSubmitError(null);
      reset({ ...DEFAULT_VALUES, fecha_analisis: new Date() });
    }
  }, [open, uidAnalisisProp, uidProtocolo, reset, loadDraft]);

  // Auto-save draft in create mode
  useEffect(() => {
    if (!open || isEdit) return;
    saveDraft(uidProtocolo, {
      ...watchedAll,
      fecha_analisis: watchedAll.fecha_analisis?.toISOString() ?? '',
      fotosTemporales: fotosTemporales
        .filter((f) => f.uploadStatus === 'done' && f.uid_foto !== '')
        .map((f) => ({ localId: f.localId, uid_foto: f.uid_foto, content_type: f.file.type, caption: f.caption })),
    });
  }, [watchedAll, fotosTemporales, open, isEdit, saveDraft, uidProtocolo]);

  // Populate form in edit mode
  useEffect(() => {
    if (isEdit && ectoQuery.data) {
      const e = ectoQuery.data;
      setActiveNumeroAnalisis(e.numero_analisis);
      reset({
        fecha_analisis: parseISO(e.fecha_analisis),
        identificacion_muestras: e.identificacion_muestras,
        muestra: e.muestras_ectoparasitos[0] ?? DEFAULT_VALUES.muestra,
      });
      setTabActiva('datos');
      setSubmitError(null);
    }
  }, [isEdit, ectoQuery.data, reset]);

  const hasAttemptedSubmit = submitCount > 0;
  const tabDatosHasError = hasAttemptedSubmit && DATOS_FIELDS.some((f) => !!errors[f]);
  const tabResultadoHasError = hasAttemptedSubmit && !!errors.muestra;

  // Warn before tab close when there are unsaved changes
  useEffect(() => {
    if (!isDirty && fotosTemporales.length === 0) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, fotosTemporales.length]);

  const isPending = createMutation.isPending || updateMutation.isPending;
  const hasPendingUploads = fotosTemporales.some((f) => f.uploadStatus === 'uploading');

  function handleClose(): void {
    const hasDirtyFotos = fotosTemporales.length > 0;
    if (isDirty || hasDirtyFotos) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  }

  function handleConfirmDiscard(): void {
    setShowDiscardDialog(false);
    onClose();
  }

  async function handleRestoreDraft(): Promise<void> {
    if (!pendingDraft) return;
    const d = pendingDraft.data;
    reset({
      ...d,
      fecha_analisis: parseISO(d.fecha_analisis),
    });
    if (d.fotosTemporales.length > 0) {
      const restoredFotos = await Promise.all(
        d.fotosTemporales.map(async (f) => {
          try {
            const { presigned_url } = await fotosTemporalesApi.getFotoTemporal(f.uid_foto);
            return {
              localId: f.localId,
              uid_foto: f.uid_foto,
              blobUrl: presigned_url,
              file: new File([], '', { type: f.content_type }),
              caption: f.caption,
              uploadStatus: 'done' as const,
            } satisfies FotoTemporal;
          } catch {
            return {
              localId: f.localId,
              uid_foto: f.uid_foto,
              blobUrl: '',
              file: new File([], '', { type: f.content_type }),
              caption: f.caption,
              uploadStatus: 'done' as const,
            } satisfies FotoTemporal;
          }
        }),
      );
      setFotosTemporales(restoredFotos);
    }
    setTabActiva('datos');
    setPendingDraft(null);
  }

  function handleDiscardDraft(): void {
    clearDraft(uidProtocolo);
    setPendingDraft(null);
  }

  function handleValidationError(): void {
    if (DATOS_FIELDS.some((f) => !!errors[f])) {
      setTabActiva('datos');
    } else if (errors.muestra) {
      setTabActiva('resultado');
    }
  }

  async function handleSave(values: EctoparasitosFormValues): Promise<void> {
    setSubmitError(null);

    const doneFotos = fotosTemporales.filter((f) => f.uploadStatus === 'done');

    try {
      if (!isEdit) {
        await createMutation.mutateAsync({
          uidProtocolo,
          data: {
            tipo_analisis: TipoAnalisis.ECTOPARASITOS,
            fecha_analisis: values.fecha_analisis.toISOString(),
            identificacion_muestras: values.identificacion_muestras,
            muestras_ectoparasitos: [values.muestra],
            fotos: doneFotos.map((f) => ({ uid_foto: f.uid_foto, content_type: f.file.type, caption: f.caption })),
          },
        });
        notify.success('Análisis creado');
        clearDraft(uidProtocolo);
        onClose();
      } else {
        await updateMutation.mutateAsync({
          uidProtocolo,
          uidAnalisis: activeUidAnalisis!,
          data: {
            fecha_analisis: values.fecha_analisis.toISOString(),
            identificacion_muestras: values.identificacion_muestras,
            muestras_ectoparasitos: [values.muestra],
          },
        });
        notify.success('Análisis actualizado');
        onClose();
      }
    } catch (err) {
      setSubmitError(err);
    }
  }

  const displayNumero =
    activeNumeroAnalisis != null
      ? activeNumeroAnalisis
      : isEdit
        ? ectoQuery.data?.numero_analisis ?? null
        : null;

  const dialogTitle = (() => {
    if (!isEdit) return 'Nuevo análisis Ectoparásitos';
    const numFmt =
      numeroProtocolo != null ? formatNumeroAnalisis(numeroProtocolo, displayNumero) : null;
    return numFmt ? `Editar análisis Ectoparásitos ${numFmt}` : 'Editar análisis Ectoparásitos';
  })();

  const subtitleParts = [
    numeroProtocolo != null ? `Protocolo N° ${numeroProtocolo}` : null,
    establecimientoNombre ?? null,
  ].filter(Boolean);
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : null;

  const isLoadingData = isEdit && ectoQuery.isLoading;

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={isPending ? undefined : handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isSmall}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            pb: subtitle ? 1 : 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>{dialogTitle}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            aria-label="Cerrar"
            size="small"
            sx={{ mt: 0.5 }}
            disabled={isPending}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tabActiva} onChange={(_, v: TabId) => setTabActiva(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab
              label="Datos"
              value="datos"
              icon={tabDatosHasError ? <ErrorOutlineIcon color="error" fontSize="small" /> : undefined}
              iconPosition="end"
            />
            <Tab
              label="Resultado"
              value="resultado"
              icon={
                tabResultadoHasError ? <ErrorOutlineIcon color="error" fontSize="small" /> : undefined
              }
              iconPosition="end"
            />
          </Tabs>
        </Box>

        {!isEdit && pendingDraft && (
          <Box sx={{ px: 3, pt: 2, pb: 2 }}>
            <Alert
              severity="info"
              sx={{
                bgcolor: 'rgba(90, 28, 112, 0.10)',
                color: '#3d1350',
                '& .MuiAlert-icon': { color: '#5A1C70' },
              }}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={() => void handleRestoreDraft()}>
                    Restaurar
                  </Button>
                  <Button size="small" color="inherit" onClick={handleDiscardDraft}>
                    Descartar
                  </Button>
                </Box>
              }
            >
              Tenés un borrador guardado{' '}
              {formatDistanceToNow(new Date(pendingDraft.timestamp), {
                addSuffix: true,
                locale: es,
              })}.
            </Alert>
          </Box>
        )}

        <DialogContent dividers>
          {submitError != null && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {String(getErrorMessage(submitError))}
            </Alert>
          )}

          {isLoadingData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={40} />
              ))}
            </Box>
          ) : (
            <>
              {tabActiva === 'datos' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <EctoparasitosDatosForm />
                  <FotosManager
                    fotos={ectoQuery.data?.fotos ?? []}
                    fotosTemporales={fotosTemporales}
                    onFotosTemporalesChange={setFotosTemporales}
                    uidProtocolo={uidProtocolo}
                    uidAnalisis={activeUidAnalisis ?? null}
                    useUploadFoto={useUploadFotoEctoparasitos}
                    useEditarCaption={useEditarCaptionEctoparasitos}
                    useEliminarFoto={useEliminarFotoEctoparasitos}
                    disabled={isPending || ectoQuery.isFetching}
                  />
                </Box>
              )}
              {tabActiva === 'resultado' && <EctoparasitosResultadoForm />}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(handleSave, handleValidationError)}
            disabled={
              isPending ||
              hasPendingUploads ||
              (isEdit && !isDirty && fotosTemporales.length === 0)
            }
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {hasPendingUploads ? 'Subiendo fotos...' : isEdit ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard confirmation */}
      <Dialog open={showDiscardDialog} maxWidth="xs" onClose={() => setShowDiscardDialog(false)}>
        <DialogTitle>Descartar cambios</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tenés cambios sin guardar. ¿Querés descartarlos y cerrar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardDialog(false)}>Volver</Button>
          <Button color="error" onClick={handleConfirmDiscard}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
